/**
 * Outline annotation geometry properties — registration and getters.
 *
 * An outline is a flat polygon-with-holes that wraps the rooms of a single
 * floor. The outer ring is the building's perimeter; cavity rings are
 * interior holes (courtyards, light wells).
 *
 * Properties registered here:
 * - outline_outer_ring (FLOAT_ARRAY, PROJECT_GLOBAL) — flat [x1,y1,z1, x2,y2,z2, ...]
 * - outline_cavities (JSON, PROJECT_GLOBAL) — JSON-stringified Coordinate[][]
 * - outline_floor_height (FLOAT, PROJECT_GLOBAL) — Z of the outline's plane
 */

import { PropertyType, PropertyValueType } from '../../types';
import { propertyRegistry } from '../../PropertyRegistry';
import { getGlobalPropertyService } from '../../reactive/globalPropertyService';
import { ReactiveProperty } from '../../reactive/ReactiveProperty';
import { TypedFloatArrayProperty } from '../../reactive/TypedFloatArrayProperty';
import { TypedJsonProperty } from '../../reactive/TypedJsonProperty';
import type { Coordinate } from './RoomGeometryPropertyGetters';

// --- Encode/decode helpers ---

function encodeRing(coords: Coordinate[]): number[] {
  const arr = new Array(coords.length * 3);
  for (let i = 0; i < coords.length; i++) {
    arr[i * 3] = coords[i].x;
    arr[i * 3 + 1] = coords[i].y;
    arr[i * 3 + 2] = coords[i].z;
  }
  return arr;
}

function decodeRing(raw: unknown): Coordinate[] {
  if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === 'number') {
    const coords: Coordinate[] = [];
    for (let i = 0; i + 2 < raw.length; i += 3) {
      coords.push({ x: raw[i], y: raw[i + 1], z: raw[i + 2] });
    }
    return coords;
  }
  if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === 'object') {
    return raw as Coordinate[];
  }
  if (typeof raw === 'string') {
    try {
      return decodeRing(JSON.parse(raw));
    } catch {
      return [];
    }
  }
  return [];
}

// --- Registration ---

export function registerOutlineGeometryProperties() {
  if (!propertyRegistry.isRegistered('outline_outer_ring')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'outline_outer_ring',
      name: 'Outer ring',
      valueType: PropertyValueType.FLOAT_ARRAY,
      defaultValue: [],
      annotationTypes: [],
    });
  }

  if (!propertyRegistry.isRegistered('outline_cavities')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'outline_cavities',
      name: 'Cavities',
      valueType: PropertyValueType.JSON,
      defaultValue: '[]',
      annotationTypes: [],
    });
  }

  if (!propertyRegistry.isRegistered('outline_floor_height')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'outline_floor_height',
      name: 'Floor height',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 0,
      annotationTypes: [],
    });
  }
}

registerOutlineGeometryProperties();

// --- Getters ---

const outlineOuterRingCache = new Map<string, TypedFloatArrayProperty<Coordinate[]>>();

export function getOutlineOuterRingProperty(annotationId: string): TypedFloatArrayProperty<Coordinate[]> {
  const cached = outlineOuterRingCache.get(annotationId);
  if (cached && !cached.isDestroyed) return cached;

  const service = getGlobalPropertyService();
  const raw = service.getProperty('outline_outer_ring', annotationId, PropertyType.PROJECT_GLOBAL);
  const typed = new TypedFloatArrayProperty<Coordinate[]>(raw, encodeRing, decodeRing);
  outlineOuterRingCache.set(annotationId, typed);
  return typed;
}

const outlineCavitiesCache = new Map<string, TypedJsonProperty<Coordinate[][]>>();

export function getOutlineCavitiesProperty(annotationId: string): TypedJsonProperty<Coordinate[][]> {
  const cached = outlineCavitiesCache.get(annotationId);
  if (cached && !cached.isDestroyed) return cached;

  const service = getGlobalPropertyService();
  const raw = service.getProperty('outline_cavities', annotationId, PropertyType.PROJECT_GLOBAL);
  const typed = new TypedJsonProperty<Coordinate[][]>(raw);
  outlineCavitiesCache.set(annotationId, typed);
  return typed;
}

export function getOutlineFloorHeightProperty(annotationId: string): ReactiveProperty<number> {
  const service = getGlobalPropertyService();
  return service.getProperty('outline_floor_height', annotationId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<number>;
}
